import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Badge from "@/components/ui/badge";
import { OnboardingStageWorkflow } from "@/pages/settings/components/contractsSettings/types";
import { deserializeResponses, useWorkflowResponses } from "@/pages/contracts/hooks";
import { useWorkflowScoreStatuses } from "@/entities/settings/hooks";
import { OnboardingWorkflowResponseValue, WorkflowScoreStatus } from "../types";
import { resolveWorkflowScoreStatus } from "../scoreStatus";
import { isPublishedOnboardingItem } from "@/pages/settings/components/contractsSettings/onboardingStudio/workflow/helpers";

interface Props {
  contractId: string;
  workflow: OnboardingStageWorkflow;
  isPreviousStage: boolean;
}

const OnboardingWorkflowStatusBadge: React.FC<Props> = ({
  contractId,
  workflow
}) => {
  const { t } = useTranslation();
  const { data } = useWorkflowResponses(contractId, workflow.id);
  const { data: scoreStatusesData } = useWorkflowScoreStatuses();

  const responses = useMemo(
    () =>
      deserializeResponses(data?.responses) as Record<
        string,
        OnboardingWorkflowResponseValue
      >,
    [data]
  );
  const scoreStatus = resolveWorkflowScoreStatus(
    (workflow.items ?? []).filter(isPublishedOnboardingItem),
    responses,
    data?.scoreStatus ?? null
  );

  const getStatusName = (id: number): string | null =>
    scoreStatusesData?.scoreStatuses?.find((statusItem) => statusItem.id === id)?.name ?? null;

  if (
    scoreStatus === WorkflowScoreStatus.REQUIRED_COMPLETED ||
    scoreStatus === WorkflowScoreStatus.COMPLETED
  ) {
    const name =
      getStatusName(scoreStatus) ??
      t(
        scoreStatus === WorkflowScoreStatus.COMPLETED
          ? "contracts.detailedView.onboarding.statusCompleted"
          : "contracts.detailedView.onboarding.statusRequiredCompleted"
      );
    return (
      <Badge
        text={name}
        textVariant="smBody"
        className="border-none gap-0 rounded-md h-7 whitespace-nowrap px-2 py-1 bg-[rgba(var(--surface-chips-positive-rgb),0.18)]"
        textClassName="text-charts-positive"
      />
    );
  }

  if (scoreStatus === WorkflowScoreStatus.IN_PROGRESS) {
    const name =
      getStatusName(scoreStatus) ??
      t("contracts.detailedView.onboarding.statusInProgress");
    return (
      <Badge
        text={name}
        textVariant="smBody"
        className="border-none gap-0 rounded-md h-7 whitespace-nowrap px-2 py-1 bg-[rgba(var(--surface-chips-natural-rgb),0.20)]"
        textClassName="text-link"
      />
    );
  }

  return null;
};

export default OnboardingWorkflowStatusBadge;
